import React, { useState, useEffect } from 'react';
import { motivoLegivel } from '@/services/erroLegivel';
import {
  strategistService,
  type StrategicBoardResponse
} from '@/services/strategistService';

export const StrategicBoard: React.FC = () => {
  const [boardData, setBoardData] = useState<StrategicBoardResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [erro, setErro] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | undefined>(undefined);
  const [selectedCategory, setSelectedCategory] = useState<string>('Todas');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  // Form State para Nova Campanha
  const [newTitle, setNewTitle] = useState('');
  const [newCategory, setNewCategory] = useState('Varejo & E-Commerce');
  const [newPriority, setNewPriority] = useState<'CRÍTICA' | 'ALTA' | 'MÉDIA'>('ALTA');
  // Sem valor pre-preenchido: 450,00 aparecia em toda campanha como se
  // alguem tivesse apurado esse ticket.
  const [newTicket, setNewTicket] = useState<string>('');
  const [newOcasiao, setNewOcasiao] = useState<string>('');
  const [newDesc, setNewDesc] = useState('');

  const fetchBoard = async () => {
    setLoading(true);
    try {
      const data = await strategistService.getStrategicBoard(selectedMonth, selectedCategory);
      setBoardData(data);
    } catch (err) {
      // Sem estado de erro visivel, a falha deixava o dado nulo e a tela
      // ficava em "carregando" para sempre — que foi como as telas do
      // Estrategista se apresentaram durante toda a auditoria.
      setErro(motivoLegivel(err));
      console.error('Erro ao carregar Kanban Estratégico:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBoard();
  }, [selectedMonth, selectedCategory]);

  const handleMoveLane = async (cardId: string, newLane: string) => {
    try {
      await strategistService.updateCardLane(cardId, newLane);
      fetchBoard();
      showToast('Campanha movida de etapa com sucesso.');
    } catch {
      showToast('Erro ao mover campanha.');
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    if (!confirm('Deseja realmente arquivar esta campanha estratégica?')) return;
    try {
      await strategistService.deleteCard(cardId);
      fetchBoard();
      showToast('Campanha arquivada.');
    } catch {
      showToast('Erro ao arquivar campanha.');
    }
  };

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    try {
      // Saíram daqui: `volume_mercado_anual: "Mercado Nacional"`, `affiliates:
      // ['Amazon','Shopee']` e `impacto_estimado_rs` com comissão estimada.
      // Entravam em TODA campanha sem ninguém ter pedido — o usuário via
      // números que não digitou e que ninguém apurou.
      await strategistService.createStrategicCard({
        titulo: newTitle,
        categoria: newCategory,
        prioridade: newPriority,
        descricao: newDesc,
        raia: 'radar_ignicao',
        ocasiao_id: newOcasiao || null,
        ticket_medio_rs: newTicket ? Number(newTicket) : null
      });
      setIsModalOpen(false);
      setNewTitle('');
      setNewDesc('');
      fetchBoard();
      showToast('Nova campanha estratégica lançada com sucesso.');
    } catch {
      showToast('Erro ao criar campanha estratégica.');
    }
  };

  const showToast = (msg: string) => {
  // F-411 (familia): o codemod de i18n tinha colado um useTranslation()
  // DENTRO desta funcao — hook fora do ciclo de render, "Invalid hook
  // call" a cada chamada. Era por isso que toasts/acoes daqui morriam em
  // silencio. O `t` nem era usado.
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 4000);
  };

  // As raias vinham escritas AQUI, e as chaves divergiam das do backend — o
  // quadro só funcionava por coincidência de nomes. Agora vêm do servidor, que
  // é quem sabe quantas são e como se chamam.
  const CORES = ['var(--chart-serie-5)', 'var(--nexus-brand)', 'var(--warn)',
                 'var(--sig-ok-text)', 'var(--text-3)'];
  const lanesOrder: Array<{ key: string; label: string; color: string; badgeBg: string }> =
    (boardData?.lanes_meta ?? []).map((l, i) => ({
    key: l.chave,
    label: l.label,
    color: CORES[i % CORES.length],
    badgeBg: 'var(--bg-2)'
  }));

  return (
    <div style={{ padding: '24px 32px', maxWidth: '1800px', margin: '0 auto', color: 'var(--text-1)' }}>
      {/* Toast Notification */}
      {toastMsg && (
        <div style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          backgroundColor: 'var(--bg-1)',
          border: '1px solid var(--sig-info-border)',
          color: 'var(--sig-info-text)',
          padding: '12px 20px',
          borderRadius: 'var(--r-md)',
          fontWeight: 600,
          fontSize: 'var(--fs-sm)',
          boxShadow: 'var(--shadow-3)',
          zIndex: 1300
        }}>
          {toastMsg}
        </div>
      )}

      {/* Header Corporativo do Kanban */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '16px', borderBottom: '1px solid var(--border-1)', paddingBottom: '18px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 style={{ fontSize: '20px', fontWeight: 800, margin: 0, color: 'var(--text-1)', letterSpacing: '-0.02em' }}>
              Quadro Kanban Estratégico Multiescala
            </h1>
            <span style={{
              backgroundColor: 'var(--nexus-brand-bg)',
              color: 'var(--nexus-brand)',
              border: '1px solid var(--border-2)',
              padding: '2px 8px',
              borderRadius: 'var(--r-sm)',
              fontSize: 'var(--fs-xs)',
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase'
            }}>
              Visão de Negócios
            </span>
          </div>
          <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-3)', margin: '4px 0 0 0', lineHeight: 1.5 }}>
            Gestão de campanhas por ciclos de maturação (D-60 a D-0). Desconectado da Redação Operacional.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <a
            href="/inteligencia/playbook"
            style={{
              padding: '8px 14px',
              backgroundColor: 'var(--bg-1)',
              color: 'var(--text-1)',
              border: '1px solid var(--border-2)',
              borderRadius: 'var(--r-md)',
              textDecoration: 'none',
              fontSize: 'var(--fs-sm)',
              fontWeight: 600
            }}
          >
            Playbook 52 Semanas
          </a>

          <button
            onClick={() => setIsModalOpen(true)}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--nexus-brand)',
              color: 'var(--text-on-accent)',
              border: 'none',
              borderRadius: 'var(--r-md)',
              fontSize: 'var(--fs-sm)',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            Nova Campanha
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <select
          value={selectedMonth || ''}
          onChange={(e) => setSelectedMonth(e.target.value ? Number(e.target.value) : undefined)}
          style={{
            backgroundColor: 'var(--bg-1)',
            border: '1px solid var(--border-1)',
            color: 'var(--text-1)',
            padding: '8px 12px',
            borderRadius: 'var(--r-md)',
            fontSize: 'var(--fs-sm)',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          <option value="">Filtro: Todos os Meses</option>
          <option value="1">Janeiro (Clima & Aulas)</option>
          <option value="4">Abril (Páscoa & 13º INSS)</option>
          <option value="5">Maio (Mães & IRPF 1)</option>
          <option value="8">Agosto (Pais & IRPF 4)</option>
          <option value="10">Outubro (Crianças)</option>
          <option value="11">Novembro (Black Friday)</option>
          <option value="12">Dezembro (Natal & Réveillon)</option>
        </select>

        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{
            backgroundColor: 'var(--bg-1)',
            border: '1px solid var(--border-1)',
            color: 'var(--text-1)',
            padding: '8px 12px',
            borderRadius: 'var(--r-md)',
            fontSize: 'var(--fs-sm)',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          <option value="Todas">Filtro: Todas Categorias</option>
          <option value="Varejo & E-Commerce">Varejo & E-Commerce</option>
          <option value="Economia & Finanças">Economia & Finanças</option>
          <option value="Turismo & Viagens">Turismo & Viagens</option>
          <option value="Comercial & Patrocínio">Comercial & Patrocínio</option>
        </select>
      </div>

      {/* Kanban Board Grid */}
      {erro ? (
        <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--text-3)' }}>
          <p style={{ color: 'var(--text-1)', fontWeight: 600, marginBottom: '6px' }}>
            Não foi possível carregar o pipeline estratégico.
          </p>
          <p style={{ fontSize: '13px' }}>{erro}</p>
        </div>
      ) : loading || !boardData ? (
        <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-3)' }}>
          Carregando pipeline estratégico...
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, minmax(280px, 1fr))',
          gap: '20px',
          alignItems: 'start',
          overflowX: 'auto',
          paddingBottom: '20px'
        }}>
          {lanesOrder.map((lane) => (
            <div key={lane.key} style={{
              backgroundColor: 'var(--bg-1)',
              borderRadius: 'var(--r-lg)',
              border: '1px solid var(--border-1)',
              display: 'flex',
              flexDirection: 'column',
              minHeight: '600px',
              maxHeight: '80vh'
            }}>
              <div style={{
                padding: '16px',
                borderBottom: '1px solid var(--border-1)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'sticky',
                top: 0,
                backgroundColor: 'var(--bg-1)',
                borderTopLeftRadius: 'var(--r-lg)',
                borderTopRightRadius: 'var(--r-lg)',
                zIndex: 10
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: lane.color }} />
                  <h3 style={{ fontSize: 'var(--fs-sm)', fontWeight: 800, margin: 0, color: 'var(--text-1)', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                    {lane.label}
                  </h3>
                </div>
                <div style={{
                  backgroundColor: lane.badgeBg,
                  color: lane.color,
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: 'var(--fs-xs)',
                  fontWeight: 700
                }}>
                  {(boardData.columns[lane.key] as any[]).length}
                </div>
              </div>

              <div style={{ padding: '16px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {(boardData.columns[lane.key] as any[]).map((card: any) => (
                  <div key={card.id} style={{
                    backgroundColor: 'var(--bg-0)',
                    border: '1px solid var(--border-2)',
                    borderRadius: 'var(--r-md)',
                    padding: '16px',
                    boxShadow: 'var(--shadow-1)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                      <span style={{ fontSize: 'var(--fs-xxs)', color: 'var(--text-4)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {card.categoria}
                      </span>
                      <span style={{
                        fontSize: 'var(--fs-xxs)',
                        padding: '2px 6px',
                        borderRadius: 'var(--r-sm)',
                        backgroundColor: card.prioridade === 'CRÍTICA' ? 'var(--sig-crit-bg)' : 'var(--bg-2)',
                        color: card.prioridade === 'CRÍTICA' ? 'var(--sig-crit-text)' : 'var(--text-3)',
                        border: `1px solid ${card.prioridade === 'CRÍTICA' ? 'var(--sig-crit-border)' : 'var(--border-2)'}`,
                        fontWeight: 700
                      }}>
                        {card.prioridade}
                      </span>
                    </div>

                    <h4 style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--text-1)', margin: 0, lineHeight: 1.4 }}>
                      {card.titulo}
                    </h4>

                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-2)', lineHeight: 1.5 }}>
                      {card.descricao}
                    </div>

                    <div style={{ backgroundColor: 'var(--bg-1)', border: '1px solid var(--border-1)', borderRadius: 'var(--r-sm)', padding: '10px' }}>
                      <div style={{ fontSize: 'var(--fs-xxs)', color: 'var(--text-3)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 600 }}>Mercado Total</div>
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-1)', fontWeight: 700 }}>{card.volume_mercado_anual}</div>
                      
                      <div style={{ fontSize: 'var(--fs-xxs)', color: 'var(--text-3)', marginTop: '8px', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 600 }}>Ticket Médio</div>
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-1)', fontWeight: 700 }}>R$ {card.ticket_medio_rs?.toFixed(2)}</div>
                    </div>

                    <div style={{
                      backgroundColor: 'var(--sig-ok-bg)',
                      border: '1px solid var(--sig-ok-border)',
                      borderRadius: 'var(--r-sm)',
                      padding: '8px 10px',
                      fontSize: 'var(--fs-xs)',
                      color: 'var(--sig-ok-text)',
                      fontWeight: 600
                    }}>
                      Impacto: {card.impacto_estimado_rs}
                    </div>

                    <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid var(--border-2)', paddingTop: '12px', marginTop: '4px' }}>
                      <select
                        value={lane.key}
                        onChange={(e) => handleMoveLane(card.id, e.target.value)}
                        style={{
                          flex: 1,
                          backgroundColor: 'var(--bg-1)',
                          border: '1px solid var(--border-2)',
                          color: 'var(--text-1)',
                          borderRadius: 'var(--r-sm)',
                          padding: '6px',
                          fontSize: 'var(--fs-xs)',
                          cursor: 'pointer'
                        }}
                      >
                        {lanesOrder.map((l) => (
                          <option key={l.key} value={l.key}>{l.label}</option>
                        ))}
                      </select>

                      <button
                        onClick={() => handleDeleteCard(card.id)}
                        title="Arquivar Campanha"
                        style={{
                          backgroundColor: 'var(--bg-1)',
                          border: '1px solid var(--border-2)',
                          color: 'var(--sig-crit-text)',
                          borderRadius: 'var(--r-sm)',
                          padding: '6px 10px',
                          cursor: 'pointer',
                          fontWeight: 700
                        }}
                      >
                        X
                      </button>
                    </div>

                  </div>
                ))}

                {(boardData.columns[lane.key] as any[]).length === 0 && (
                  <div style={{
                    padding: '30px 16px',
                    textAlign: 'center',
                    border: '1px dashed var(--border-2)',
                    borderRadius: 'var(--r-md)',
                    color: 'var(--text-3)',
                    fontSize: 'var(--fs-xs)',
                    backgroundColor: 'var(--bg-0)'
                  }}>
                    Pipeline livre para injeção de novas pautas e campanhas.
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Nova Campanha */}
      {isModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'var(--bg-overlay)',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'var(--bg-0)',
            border: '1px solid var(--border-1)',
            borderRadius: 'var(--r-lg)',
            width: '100%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: '32px',
            boxShadow: 'var(--shadow-3)'
          }}>
            <h2 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--text-1)', margin: '0 0 24px 0', borderBottom: '1px solid var(--border-1)', paddingBottom: '16px' }}>
              Criar Nova Campanha Estratégica
            </h2>
            
            <form onSubmit={handleCreateCampaign} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--text-2)' }}>Título da Campanha (H1):</label>
                <input
                  required
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  style={{ padding: '10px 12px', borderRadius: 'var(--r-md)', border: '1px solid var(--border-2)', backgroundColor: 'var(--bg-1)', color: 'var(--text-1)', fontSize: 'var(--fs-sm)' }}
                  placeholder="Ex: Top 5 Geladeiras Inverse..."
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--text-2)' }}>Categoria:</label>
                  <select value={newCategory} onChange={(e) => setNewCategory(e.target.value)} style={{ padding: '10px 12px', borderRadius: 'var(--r-md)', border: '1px solid var(--border-2)', backgroundColor: 'var(--bg-1)', color: 'var(--text-1)', fontSize: 'var(--fs-sm)' }}>
                    <option>Varejo & E-Commerce</option>
                    <option>Economia & Finanças</option>
                    <option>Turismo & Viagens</option>
                    <option>Comercial & Patrocínio</option>
                  </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--text-2)' }}>Prioridade Estratégica:</label>
                  <select value={newPriority} onChange={(e: any) => setNewPriority(e.target.value)} style={{ padding: '10px 12px', borderRadius: 'var(--r-md)', border: '1px solid var(--border-2)', backgroundColor: 'var(--bg-1)', color: 'var(--text-1)', fontSize: 'var(--fs-sm)' }}>
                    <option value="CRÍTICA">CRÍTICA (Obrigatória)</option>
                    <option value="ALTA">ALTA</option>
                    <option value="MÉDIA">MÉDIA</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--text-2)' }}>
                  Ocasião do calendário (opcional):
                </label>
                <input
                  value={newOcasiao}
                  onChange={(e) => setNewOcasiao(e.target.value)}
                  placeholder="ex.: black_friday"
                  style={{ padding: '10px 12px', borderRadius: 'var(--r-md)', border: '1px solid var(--border-2)', backgroundColor: 'var(--bg-1)', color: 'var(--text-1)', fontSize: 'var(--fs-sm)' }}
                />
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-3)' }}>
                  Ligando a campanha a uma ocasião, os prazos de publicar e de
                  apresentar ao patrocinador saem da antecedência medida dela.
                  Em branco, a campanha fica sem prazo — o que é honesto, porque
                  não há o que medir.
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--text-2)' }}>Ticket Médio (R$):</label>
                  <input type="number" step="0.01" value={newTicket} onChange={(e) => setNewTicket(e.target.value)} placeholder="opcional" style={{ padding: '10px 12px', borderRadius: 'var(--r-md)', border: '1px solid var(--border-2)', backgroundColor: 'var(--bg-1)', color: 'var(--text-1)', fontSize: 'var(--fs-sm)' }} />
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--text-2)' }}>Volume Total Mercado:</label>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--text-2)' }}>Descrição do Escopo / Briefing Executivo:</label>
                <textarea
                  required
                  rows={4}
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  style={{ padding: '10px 12px', borderRadius: 'var(--r-md)', border: '1px solid var(--border-2)', backgroundColor: 'var(--bg-1)', color: 'var(--text-1)', fontSize: 'var(--fs-sm)', fontFamily: 'inherit' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-1)' }}>
                <button type="button" onClick={() => setIsModalOpen(false)} style={{ padding: '10px 20px', backgroundColor: 'var(--bg-2)', border: '1px solid var(--border-2)', borderRadius: 'var(--r-md)', color: 'var(--text-1)', fontWeight: 600, cursor: 'pointer' }}>
                  Cancelar
                </button>
                <button type="submit" style={{ padding: '10px 20px', backgroundColor: 'var(--nexus-brand)', border: 'none', borderRadius: 'var(--r-md)', color: 'var(--text-on-accent)', fontWeight: 700, cursor: 'pointer' }}>
                  Criar Campanha
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
